<template>
	<div class="m-tools">
		<div class="tools-bar">
			<el-input v-model="keyword" placeholder="搜索工具" size="small" clearable class="tools-search">
				<i slot="prefix" class="el-input__icon el-icon-search"></i>
			</el-input>
			<div class="tools-filter">
				<span class="filter-item" :class="{active: category === ''}" @click="category = ''">全部</span>
				<span class="filter-item" v-for="item in categories" :key="item.key"
				      :class="{active: category === item.key}" @click="category = item.key">{{ item.name }}</span>
			</div>
		</div>

		<div v-loading="loading" class="tools-grid">
			<div class="tool-card" v-for="tool in filteredTools" :key="tool.toolKey"
			     :class="{active: current && current.toolKey === tool.toolKey}" @click="select(tool)">
				<i class="tool-icon" :class="iconOf(tool)"></i>
				<div class="tool-text">
					<div class="tool-name">{{ tool.name }}</div>
					<div class="tool-desc">{{ tool.description }}</div>
				</div>
			</div>
			<div v-if="!loading && !filteredTools.length" class="tools-empty">没有匹配的工具</div>
		</div>

		<div v-if="current" ref="panel" class="tool-panel">
			<div class="panel-head">
				<div>
					<span class="panel-title">{{ current.name }}</span>
					<span class="panel-sub">{{ current.description }}</span>
				</div>
				<i class="close icon panel-close" @click="current = null"></i>
			</div>

			<!-- 单输入：一段输入 + 若干动作 -->
			<template v-if="impl && impl.kind === 'text'">
				<el-input type="textarea" v-model="input" :rows="6" :placeholder="impl.placeholder" resize="vertical"/>
				<div class="panel-actions">
					<el-button v-for="action in impl.actions" :key="action.key" size="small"
					           @click="runText(action)">{{ action.label }}
					</el-button>
				</div>
			</template>

			<!-- 双输入：正则测试、文本对比 -->
			<template v-else-if="impl && impl.kind === 'dual'">
				<div class="dual-label">{{ impl.primaryLabel }}</div>
				<el-input v-if="impl.key === 'regex'" v-model="primary" :placeholder="impl.primaryPlaceholder"/>
				<el-input v-else type="textarea" v-model="primary" :rows="5" :placeholder="impl.primaryPlaceholder" resize="vertical"/>

				<div v-if="impl.flagOptions" class="dual-flags">
					<span class="dual-label inline">修饰符</span>
					<el-checkbox-group v-model="flags" size="mini">
						<el-checkbox-button v-for="f in impl.flagOptions" :key="f" :label="f">{{ f }}</el-checkbox-button>
					</el-checkbox-group>
				</div>

				<div class="dual-label">{{ impl.secondaryLabel }}</div>
				<el-input type="textarea" v-model="secondary" :rows="5" :placeholder="impl.secondaryPlaceholder" resize="vertical"/>

				<div class="panel-actions">
					<el-button v-for="action in impl.actions" :key="action.key" size="small"
					           @click="runDual(action)">{{ action.label }}
					</el-button>
				</div>
			</template>

			<!-- 图片压缩 -->
			<template v-else-if="impl && impl.kind === 'image'">
				<div class="panel-actions">
					<el-button size="small" @click="pickFile('image')">选择图片</el-button>
					<span class="quality-label">质量 {{ Math.round(quality * 100) }}%</span>
					<el-slider v-model="quality" :min="0.1" :max="1" :step="0.05" class="quality-slider" @change="compress"/>
				</div>
				<div v-if="imageResult" class="image-result">
					<img :src="imageResult.url" class="image-preview" alt="压缩预览"/>
					<div class="image-meta">
						<div>原图：{{ formatSize(imageResult.rawSize) }}（{{ imageResult.width }} × {{ imageResult.height }}）</div>
						<div>压缩后：{{ formatSize(imageResult.size) }}</div>
						<div class="image-saved">节省 {{ imageResult.saved }}%</div>
						<el-button size="mini" type="primary" plain @click="download">下载</el-button>
					</div>
				</div>
			</template>

			<!-- 扫码识别 -->
			<template v-else-if="impl && impl.kind === 'scan'">
				<div class="panel-actions">
					<el-button size="small" @click="pickFile('scan')">选择二维码图片</el-button>
				</div>
				<p class="panel-tip" v-if="!scanSupported">
					当前浏览器不支持原生条码识别（需要 Chrome / Edge 等），换个浏览器再试。
				</p>
			</template>

			<pre v-if="output" class="panel-output">{{ output }}</pre>
			<div v-if="output" class="panel-actions">
				<el-button size="mini" type="primary" plain @click="copy">复制结果</el-button>
				<el-button size="mini" plain @click="output = ''">清空</el-button>
			</div>
		</div>

		<input ref="file" type="file" accept="image/*" class="hidden-file" @change="onFile"/>

		<p class="tools-note">
			<i class="lock icon"></i>所有计算都在你的浏览器里完成，输入的内容不会上传到服务器。
		</p>
	</div>
</template>

<script>
	import {getToolList, reportToolUse} from '@/api/wxtools'
	import {getTool} from '@/util/tools/registry'

	const CATEGORY_NAMES = {
		format: '格式化',
		convert: '转换',
		encode: '编解码',
		generate: '生成',
		common: '通用'
	}

	// 后端下发的是小程序侧的图标名，这里映射到 Semantic UI 的图标
	const ICONS = {
		json: 'code', timestamp: 'clock outline', regex: 'search', base64: 'exchange',
		url: 'linkify', md5: 'hashtag', uuid: 'random', radix: 'calculator',
		color: 'paint brush', text: 'font', cron: 'calendar alternate outline',
		diff: 'columns', image: 'image outline', scan: 'qrcode'
	}

	export default {
		name: 'ToolBox',
		data() {
			return {
				loading: true,
				tools: [],
				keyword: '',
				category: '',
				current: null,
				input: '',
				primary: '',
				secondary: '',
				flags: ['g'],
				output: '',
				quality: 0.7,
				imageResult: null,
				rawFile: null,
				fileFor: 'image',
				scanSupported: typeof window !== 'undefined' && 'BarcodeDetector' in window
			}
		},
		computed: {
			impl() {
				return this.current ? getTool(this.current.toolKey) : null
			},
			categories() {
				const keys = []
				this.tools.forEach((tool) => {
					if (tool.category && keys.indexOf(tool.category) === -1) {
						keys.push(tool.category)
					}
				})
				return keys.map((key) => ({key, name: CATEGORY_NAMES[key] || key}))
			},
			filteredTools() {
				const kw = this.keyword.trim().toLowerCase()
				return this.tools.filter((tool) => {
					if (this.category && tool.category !== this.category) {
						return false
					}
					if (!kw) {
						return true
					}
					return (tool.name + tool.description + tool.toolKey).toLowerCase().indexOf(kw) > -1
				})
			}
		},
		created() {
			this.loadTools()
		},
		methods: {
			loadTools() {
				getToolList().then((res) => {
					if (res.code === 200) {
						// 后台可能上架了前端还没实现的工具，过滤掉避免点开是空的
						this.tools = (res.data || []).filter((tool) => getTool(tool.toolKey))
					}
				}).catch(() => {
					this.$message.error('工具清单加载失败')
				}).finally(() => {
					this.loading = false
				})
			},
			iconOf(tool) {
				return (ICONS[tool.toolKey] || 'wrench') + ' icon'
			},
			select(tool) {
				this.current = tool
				this.input = ''
				this.primary = ''
				this.secondary = ''
				this.output = ''
				this.imageResult = null
				this.rawFile = null
				reportToolUse(tool.toolKey).catch(() => {
					// 使用量统计失败不影响工具本身
				})
				this.$nextTick(() => {
					this.$refs.panel && this.$refs.panel.scrollIntoView({behavior: 'smooth', block: 'nearest'})
				})
			},
			runText(action) {
				try {
					this.output = action.run(this.input)
				} catch (e) {
					this.output = ''
					this.$message.error(e.message || '处理失败')
				}
			},
			runDual(action) {
				try {
					this.output = action.run(this.primary, this.secondary, this.flags.join(''))
				} catch (e) {
					this.output = ''
					this.$message.error(e.message || '处理失败')
				}
			},
			pickFile(usage) {
				this.fileFor = usage
				this.$refs.file.value = ''
				this.$refs.file.click()
			},
			onFile(e) {
				const file = e.target.files && e.target.files[0]
				if (!file) {
					return
				}
				if (this.fileFor === 'image') {
					this.rawFile = file
					this.compress()
				} else {
					this.scan(file)
				}
			},
			compress() {
				if (!this.rawFile) {
					return
				}
				const file = this.rawFile
				const reader = new FileReader()
				reader.onload = () => {
					const img = new Image()
					img.onload = () => {
						const canvas = document.createElement('canvas')
						canvas.width = img.width
						canvas.height = img.height
						canvas.getContext('2d').drawImage(img, 0, 0)
						canvas.toBlob((blob) => {
							if (!blob) {
								this.$message.error('压缩失败')
								return
							}
							this.imageResult && URL.revokeObjectURL(this.imageResult.url)
							this.imageResult = {
								url: URL.createObjectURL(blob),
								blob,
								size: blob.size,
								rawSize: file.size,
								width: img.width,
								height: img.height,
								saved: Math.max(0, Math.round((1 - blob.size / file.size) * 100))
							}
						}, 'image/jpeg', this.quality)
					}
					img.onerror = () => this.$message.error('图片解析失败')
					img.src = reader.result
				}
				reader.readAsDataURL(file)
			},
			download() {
				if (!this.imageResult) {
					return
				}
				const a = document.createElement('a')
				a.href = this.imageResult.url
				a.download = 'compressed.jpg'
				a.click()
			},
			scan(file) {
				if (!this.scanSupported) {
					this.$message.error('当前浏览器不支持二维码识别')
					return
				}
				createImageBitmap(file).then((bitmap) => {
					// eslint-disable-next-line no-undef
					return new BarcodeDetector().detect(bitmap)
				}).then((codes) => {
					this.output = codes.length
						? codes.map((c, i) => `[${i + 1}] ${c.format}\n${c.rawValue}`).join('\n\n')
						: '没有识别到二维码'
				}).catch(() => {
					this.$message.error('识别失败，换一张更清晰的图片试试')
				})
			},
			copy() {
				const done = () => this.$message.success('已复制')
				if (navigator.clipboard) {
					navigator.clipboard.writeText(this.output).then(done).catch(() => this.fallbackCopy())
				} else {
					this.fallbackCopy()
				}
			},
			fallbackCopy() {
				const area = document.createElement('textarea')
				area.value = this.output
				document.body.appendChild(area)
				area.select()
				document.execCommand('copy')
				document.body.removeChild(area)
				this.$message.success('已复制')
			},
			formatSize(bytes) {
				if (bytes < 1024) {
					return bytes + ' B'
				}
				if (bytes < 1024 * 1024) {
					return (bytes / 1024).toFixed(1) + ' KB'
				}
				return (bytes / 1024 / 1024).toFixed(2) + ' MB'
			}
		},
		beforeDestroy() {
			this.imageResult && URL.revokeObjectURL(this.imageResult.url)
		}
	}
</script>

<style scoped>
	.tools-bar {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 10px;
		margin-bottom: 14px;
	}

	.tools-search {
		width: 180px;
	}

	.tools-filter {
		display: flex;
		flex-wrap: wrap;
		gap: 6px;
	}

	.filter-item {
		font-size: 13px;
		padding: 3px 10px;
		border-radius: 12px;
		color: rgba(0, 0, 0, .55);
		background: rgba(0, 0, 0, .04);
		cursor: pointer;
		transition: .2s;
	}

	.filter-item:hover {
		color: #409eff;
	}

	.filter-item.active {
		color: #fff;
		background: #409eff;
	}

	.tools-grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
		gap: 10px;
		min-height: 60px;
	}

	.tool-card {
		display: flex;
		align-items: flex-start;
		padding: 12px;
		border: 1px solid rgba(34, 36, 38, .1);
		border-radius: 6px;
		cursor: pointer;
		transition: .2s;
		background: #fff;
	}

	.tool-card:hover {
		border-color: #409eff;
		box-shadow: 0 2px 10px rgba(0, 0, 0, .06);
		transform: translateY(-1px);
	}

	.tool-card.active {
		border-color: #409eff;
		background: rgba(64, 158, 255, .05);
	}

	.tool-icon {
		font-size: 18px !important;
		color: #409eff;
		margin-right: 10px !important;
		margin-top: 2px;
		flex-shrink: 0;
	}

	.tool-text {
		min-width: 0;
	}

	.tool-name {
		font-size: 14px;
		font-weight: 500;
		color: rgba(0, 0, 0, .8);
		margin-bottom: 3px;
	}

	.tool-desc {
		font-size: 12px;
		color: rgba(0, 0, 0, .45);
		line-height: 1.4;
	}

	.tools-empty {
		grid-column: 1 / -1;
		text-align: center;
		padding: 24px 0;
		color: rgba(0, 0, 0, .35);
		font-size: 13px;
	}

	.tool-panel {
		margin-top: 16px;
		padding: 16px;
		border: 1px solid rgba(34, 36, 38, .12);
		border-radius: 6px;
		background: #fcfcfd;
	}

	.panel-head {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 12px;
	}

	.panel-title {
		font-size: 15px;
		font-weight: 500;
		color: rgba(0, 0, 0, .8);
	}

	.panel-sub {
		font-size: 12px;
		color: rgba(0, 0, 0, .4);
		margin-left: 8px;
	}

	.panel-close {
		cursor: pointer;
		color: rgba(0, 0, 0, .3);
	}

	.panel-close:hover {
		color: rgba(0, 0, 0, .6);
	}

	.panel-actions {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 8px;
		margin-top: 10px;
	}

	.panel-actions >>> .el-button + .el-button {
		margin-left: 0;
	}

	.panel-output {
		margin-top: 12px;
		padding: 12px;
		background: #f5f7fa;
		border-radius: 4px;
		font-size: 13px;
		line-height: 1.6;
		white-space: pre-wrap;
		word-break: break-all;
		max-height: 360px;
		overflow: auto;
		font-family: Consolas, Monaco, 'Courier New', monospace;
	}

	.panel-tip {
		font-size: 12px;
		color: #e6a23c;
		margin-top: 10px;
	}

	.dual-label {
		font-size: 13px;
		color: rgba(0, 0, 0, .6);
		margin: 10px 0 6px;
	}

	.dual-label.inline {
		margin: 0 10px 0 0;
	}

	.dual-flags {
		display: flex;
		align-items: center;
		margin-top: 8px;
	}

	.quality-label {
		font-size: 12px;
		color: rgba(0, 0, 0, .5);
	}

	.quality-slider {
		width: 160px;
	}

	.image-result {
		display: flex;
		gap: 16px;
		margin-top: 14px;
		flex-wrap: wrap;
	}

	.image-preview {
		max-width: 200px;
		max-height: 160px;
		border-radius: 4px;
		border: 1px solid rgba(0, 0, 0, .08);
	}

	.image-meta {
		font-size: 13px;
		color: rgba(0, 0, 0, .6);
		line-height: 1.8;
	}

	.image-saved {
		color: #67c23a;
		font-weight: 500;
		margin-bottom: 6px;
	}

	.hidden-file {
		display: none;
	}

	.tools-note {
		margin-top: 16px;
		font-size: 12px;
		color: rgba(0, 0, 0, .35);
	}

	@media screen and (max-width: 768px) {
		.tools-grid {
			grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
		}

		.tools-search {
			width: 100%;
		}
	}
</style>
